import React, { useState, useEffect, useRef } from 'react';
import { GlassCard } from '../../../_shared/GlassCard';
import { Eye, FileText, BarChart3, Trophy } from 'lucide-react';

// Scraped Mar 24 → May 13, 2026 via Apify (data/network-stats.json)
const data = {
    totalAccounts: 11,
    totalViewsAllAccounts: 536405,
    totalPostsAllAccounts: 595,
    avgViewsPerPost: 902,
    topAccount: { handle: '@unseenabhiveer', platform: 'Instagram', views: 339565 },
};

const useCounter = (end, duration = 1200) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const hasAnimated = useRef(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated.current) {
                    hasAnimated.current = true;
                    const start = performance.now();
                    const animate = (now) => {
                        const progress = Math.min(1, (now - start) / duration);
                        const eased = 1 - Math.pow(1 - progress, 3);
                        setCount(Math.floor(end * eased));
                        if (progress < 1) requestAnimationFrame(animate);
                    };
                    requestAnimationFrame(animate);
                }
            },
            { threshold: 0.3 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [end, duration]);

    return [count, ref];
};

const StatColumn = ({ icon: Icon, iconColor, label, value, displayValue, sublabel }) => {
    const [animatedValue, ref] = useCounter(typeof value === 'number' ? value : 0);
    const showAnimated = typeof value === 'number';

    return (
        <div ref={ref} className="flex flex-col items-center text-center px-4">
            <div className={`${iconColor} p-3 rounded-xl mb-3 inline-flex`}>
                <Icon size={20} />
            </div>
            <p className="text-white/50 text-[10px] uppercase tracking-widest font-semibold mb-2">{label}</p>
            <p className="text-2xl md:text-3xl font-black text-white tracking-tight">
                {displayValue ?? (showAnimated ? animatedValue.toLocaleString() : '—')}
            </p>
            {sublabel && <p className="text-white/40 text-xs mt-1.5">{sublabel}</p>}
        </div>
    );
};

export const NetworkTotals = () => {
    return (
        <section className="py-16 px-4">
            <div className="container mx-auto max-w-6xl">
                <p className="text-white/40 text-xs uppercase tracking-[0.2em] font-semibold text-center mb-3">Combined Totals</p>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 text-center">
                    All {data.totalAccounts} Accounts, One Window
                </h2>
                <p className="text-white/50 text-center mb-12 max-w-xl mx-auto">
                    The full network rolled up across 51 days.
                </p>

                <GlassCard>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-2 divide-y md:divide-y-0 md:divide-x divide-white/10">
                        <StatColumn
                            icon={Eye}
                            iconColor="bg-blue-500/20 text-blue-300"
                            label="Total Views"
                            value={data.totalViewsAllAccounts}
                            sublabel={`across ${data.totalAccounts} accounts`}
                        />
                        <StatColumn
                            icon={FileText}
                            iconColor="bg-purple-500/20 text-purple-300"
                            label="Total Posts"
                            value={data.totalPostsAllAccounts}
                            sublabel="in window"
                        />
                        <StatColumn
                            icon={BarChart3}
                            iconColor="bg-emerald-500/20 text-emerald-300"
                            label="Avg Views / Post"
                            value={data.avgViewsPerPost}
                            sublabel="network-wide"
                        />
                        <StatColumn
                            icon={Trophy}
                            iconColor="bg-amber-500/20 text-amber-300"
                            label="Top Account"
                            value={null}
                            displayValue={data.topAccount.handle}
                            sublabel={`${data.topAccount.platform} · ${data.topAccount.views.toLocaleString()} views`}
                        />
                    </div>
                </GlassCard>
            </div>
        </section>
    );
};
